/**
 * InputArea Component
 * User input with command handling using useStdin for raw input handling
 * Features: Tab completion, command suggestions, styled input box,
 *           input history (Up/Down), cursor movement (Left/Right),
 *           multi-line paste (bracketed paste mode + fast-return fallback),
 *           and multi-line input (backslash continuation)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, useStdin } from 'ink';
import { SYMBOLS, INK_COLORS, COLORS } from '../theme/ink-theme.js';

/** Maximum number of input history entries to retain */
const MAX_HISTORY = 100;

/** Debounce window for batching rapid IME composition input (ms) */
const IME_DEBOUNCE_MS = 30;

/**
 * If Enter arrives within this window after the last insertable character,
 * treat it as part of a paste (newline) rather than a submit.
 * Fallback for terminals that don't support bracketed paste mode.
 */
const FAST_RETURN_THRESHOLD_MS = 30;

const PASTE_START = '\x1b[200~';
const PASTE_END = '\x1b[201~';

interface InputAreaProps {
    onSubmit: (value: string) => void;
    onInputChange?: (value: string) => void;
    onTabComplete?: (value: string) => string | null;
    suggestions?: string[];
    disabled?: boolean;
    placeholder?: string;
}

export const InputArea: React.FC<InputAreaProps> = ({
    onSubmit,
    onInputChange,
    onTabComplete,
    suggestions = [],
    disabled = false,
    placeholder,
}) => {
    const [value, setValue] = useState('');
    const [cursorPos, setCursorPos] = useState(0);
    const cursorPosRef = useRef(cursorPos);
    const { stdin, setRawMode } = useStdin();
    const rawModeRef = useRef(true);
    const callbacksRef = useRef({ onSubmit, onInputChange, onTabComplete });

    // Input history for Up/Down navigation
    const historyRef = useRef<string[]>([]);
    const historyIndexRef = useRef(-1);
    const savedInputRef = useRef('');

    // IME composition debounce buffer
    const imeBufferRef = useRef('');
    const imeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Bracketed paste state
    const pasteBufferRef = useRef<string | null>(null);

    // Timestamp of last insertable character for fast-return heuristic
    const lastInsertTimeRef = useRef(0);

    useEffect(() => {
        callbacksRef.current = { onSubmit, onInputChange, onTabComplete };
    });

    // Keep cursorPosRef in sync with cursorPos state
    useEffect(() => {
        cursorPosRef.current = cursorPos;
    }, [cursorPos]);

    const prevValueRef = useRef(value);
    useEffect(() => {
        if (prevValueRef.current !== value) {
            prevValueRef.current = value;
            callbacksRef.current.onInputChange?.(value);
        }
    }, [value]);

    /** Insert text at cursor position (handles multi-line paste content) */
    const insertText = (text: string) => {
        setValue(currentValue => {
            const pos = cursorPosRef.current;
            const newValue = currentValue.slice(0, pos) + text + currentValue.slice(pos);
            setCursorPos(pos + text.length);
            return newValue;
        });
        historyIndexRef.current = -1;
    };

    useEffect(() => {
        setRawMode(true);
        rawModeRef.current = true;

        // Enable bracketed paste mode so the terminal wraps pasted content
        // with \x1b[200~ ... \x1b[201~ escape sequences
        process.stdout.write('\x1b[?2004h');

        const handleData = (data: Buffer) => {
            let input = data.toString();

            // Ctrl+C - pass through to App.tsx
            if (input === '\x03') {
                return;
            }

            if (disabled) return;

            // --- Bracketed paste handling ---
            // Pasted text may arrive across multiple data events, or the
            // start/end markers may be embedded in a single chunk.

            // Check for paste-start in the middle of a chunk
            const pasteStartIdx = input.indexOf(PASTE_START);
            if (pasteStartIdx !== -1) {
                // Process anything before the paste-start marker normally
                if (pasteStartIdx > 0) {
                    handleData(Buffer.from(input.slice(0, pasteStartIdx)));
                }
                // Begin buffering paste content
                pasteBufferRef.current = '';
                const afterStart = input.slice(pasteStartIdx + PASTE_START.length);
                if (afterStart.length > 0) {
                    handleData(Buffer.from(afterStart));
                }
                return;
            }

            // If we're in paste mode, buffer everything until paste-end
            if (pasteBufferRef.current !== null) {
                const pasteEndIdx = input.indexOf(PASTE_END);
                if (pasteEndIdx !== -1) {
                    pasteBufferRef.current += input.slice(0, pasteEndIdx);
                    const pastedText = pasteBufferRef.current;
                    pasteBufferRef.current = null;

                    if (pastedText.length > 0) {
                        // Normalize line endings and insert the full paste
                        insertText(pastedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));
                    }

                    // Process anything after the paste-end marker
                    const afterEnd = input.slice(pasteEndIdx + PASTE_END.length);
                    if (afterEnd.length > 0) {
                        handleData(Buffer.from(afterEnd));
                    }
                } else {
                    pasteBufferRef.current += input;
                }
                return;
            }

            // Tab key - command completion
            if (input === '\t') {
                const { onTabComplete } = callbacksRef.current;
                setValue(currentValue => {
                    if (onTabComplete && currentValue.startsWith('/')) {
                        const completed = onTabComplete(currentValue);
                        if (completed) {
                            setCursorPos(completed.length);
                            return completed;
                        }
                    }
                    return currentValue;
                });
                return;
            }

            // Arrow keys and other escape sequences
            if (input.startsWith('\x1b[') || input.startsWith('\x1b\x4f')) {
                const seq = input.startsWith('\x1b[') ? input.slice(2) : input.slice(2);

                // Up arrow - history previous
                if (seq === 'A') {
                    const history = historyRef.current;
                    if (history.length === 0) return;

                    setValue(currentValue => {
                        if (historyIndexRef.current === -1) {
                            savedInputRef.current = currentValue;
                        }
                        const newIndex = Math.min(historyIndexRef.current + 1, history.length - 1);
                        historyIndexRef.current = newIndex;
                        const historyValue = history[history.length - 1 - newIndex];
                        setCursorPos(historyValue.length);
                        return historyValue;
                    });
                    return;
                }

                // Down arrow - history next
                if (seq === 'B') {
                    setValue(() => {
                        if (historyIndexRef.current <= 0) {
                            historyIndexRef.current = -1;
                            const saved = savedInputRef.current;
                            setCursorPos(saved.length);
                            return saved;
                        }
                        historyIndexRef.current -= 1;
                        const history = historyRef.current;
                        const historyValue = history[history.length - 1 - historyIndexRef.current];
                        setCursorPos(historyValue.length);
                        return historyValue;
                    });
                    return;
                }

                // Right arrow - move cursor right
                if (seq === 'C') {
                    setCursorPos(pos => {
                        return Math.min(pos + 1, prevValueRef.current.length);
                    });
                    return;
                }

                // Left arrow - move cursor left
                if (seq === 'D') {
                    setCursorPos(pos => Math.max(pos - 1, 0));
                    return;
                }

                // Home key
                if (seq === 'H' || seq === '1~') {
                    setCursorPos(0);
                    return;
                }

                // End key
                if (seq === 'F' || seq === '4~') {
                    setCursorPos(prevValueRef.current.length);
                    return;
                }

                // Delete key
                if (seq === '3~') {
                    setValue(currentValue => {
                        const pos = cursorPosRef.current;
                        return currentValue.slice(0, pos) + currentValue.slice(pos + 1);
                    });
                    return;
                }

                return; // Ignore other escape sequences
            }

            // Plain Escape - clear input
            if (input === '\x1b') {
                setValue('');
                setCursorPos(0);
                historyIndexRef.current = -1;
                return;
            }

            // Enter - submit, backslash continuation, or fast-return paste detection
            if (input === '\r' || input === '\n') {
                const now = Date.now();
                const timeSinceLastInsert = now - lastInsertTimeRef.current;

                // Fast-return heuristic: if Enter arrives very quickly after
                // the last printable character, it's likely part of a paste
                // from a terminal that doesn't support bracketed paste mode.
                if (timeSinceLastInsert <= FAST_RETURN_THRESHOLD_MS) {
                    insertText('\n');
                    return;
                }

                setValue(currentValue => {
                    // Backslash continuation for multi-line
                    if (currentValue.endsWith('\\')) {
                        const continued = currentValue.slice(0, -1) + '\n';
                        setCursorPos(continued.length);
                        return continued;
                    }

                    if (currentValue.trim()) {
                        // Add to history
                        historyRef.current.push(currentValue.trim());
                        if (historyRef.current.length > MAX_HISTORY) {
                            historyRef.current.shift();
                        }
                        historyIndexRef.current = -1;

                        setTimeout(() => {
                            callbacksRef.current.onSubmit(currentValue.trim());
                        }, 0);
                        setCursorPos(0);
                        return '';
                    }
                    return currentValue;
                });
                return;
            }

            // Backspace
            if (input === '\x7f' || input === '\x08') {
                setValue(currentValue => {
                    const pos = cursorPosRef.current;
                    if (pos > 0) {
                        setCursorPos(pos - 1);
                        return currentValue.slice(0, pos - 1) + currentValue.slice(pos);
                    }
                    return currentValue;
                });
                return;
            }

            // Ctrl+A - move to start
            if (input === '\x01') {
                setCursorPos(0);
                return;
            }

            // Ctrl+E - move to end
            if (input === '\x05') {
                setCursorPos(prevValueRef.current.length);
                return;
            }

            // Ctrl+U - clear line before cursor
            if (input === '\x15') {
                setValue(currentValue => {
                    const pos = cursorPosRef.current;
                    const after = currentValue.slice(pos);
                    setCursorPos(0);
                    return after;
                });
                return;
            }

            // Ctrl+K - clear line after cursor
            if (input === '\x0b') {
                setValue(currentValue => currentValue.slice(0, cursorPosRef.current));
                return;
            }

            // Ctrl+W - delete word before cursor
            if (input === '\x17') {
                setValue(currentValue => {
                    const pos = cursorPosRef.current;
                    const before = currentValue.slice(0, pos);
                    const after = currentValue.slice(pos);
                    const trimmed = before.replace(/\S+\s*$/, '');
                    setCursorPos(trimmed.length);
                    return trimmed + after;
                });
                return;
            }

            // Printable characters (ASCII, CJK, emoji, etc.) and pasted text.
            // Filter out control chars (< 0x20) and escape sequences.
            const isPrintable = !input.startsWith('\x1b') &&
                input.length > 0 &&
                !Array.from(input).some(ch => ch.charCodeAt(0) < 32);

            if (isPrintable) {
                lastInsertTimeRef.current = Date.now();

                // Batch rapid IME composition input via debounced ref to prevent
                // intermediate rendering jumps during Chinese/Japanese/Korean input
                imeBufferRef.current += input;

                if (imeTimerRef.current !== null) {
                    clearTimeout(imeTimerRef.current);
                }

                imeTimerRef.current = setTimeout(() => {
                    const buffered = imeBufferRef.current;
                    imeBufferRef.current = '';
                    imeTimerRef.current = null;

                    if (buffered.length > 0) {
                        insertText(buffered);
                    }
                }, IME_DEBOUNCE_MS);
            }
        };

        stdin.on('data', handleData);

        return () => {
            stdin.off('data', handleData);
            if (imeTimerRef.current !== null) {
                clearTimeout(imeTimerRef.current);
                imeTimerRef.current = null;
            }
            imeBufferRef.current = '';
            pasteBufferRef.current = null;
            // Disable bracketed paste mode
            process.stdout.write('\x1b[?2004l');
            if (rawModeRef.current) {
                try {
                    setRawMode(false);
                    rawModeRef.current = false;
                } catch {
                    // Ignore errors during cleanup
                }
            }
        };
    }, [stdin, setRawMode, disabled]);

    // Split value for cursor rendering
    const beforeCursor = value.slice(0, cursorPos);
    const afterCursor = value.slice(cursorPos);
    const isMultiLine = value.includes('\n');

    // Render the input with a single inverse cell at the cursor. Embedded
    // `\n` chars are preserved by Ink's <Text>, so multi-line pastes and
    // continuations render across multiple visual rows automatically — the
    // inverse cell sits exactly at `cursorPos`, which may be at the end of
    // any line.
    const renderValueWithCursor = () => (
        <>
            <Text>{beforeCursor}</Text>
            <Text inverse> </Text>
            <Text>{afterCursor}</Text>
        </>
    );

    const totalSuggestions = suggestions.length;
    const visibleSuggestions = suggestions.slice(0, 6);
    const hiddenCount = Math.max(0, totalSuggestions - visibleSuggestions.length);

    return (
        <Box flexDirection="column">
            {/* Prompt line — Paean arch leads the user input */}
            <Box marginTop={0}>
                <Text color={disabled ? 'gray' : COLORS.primary} bold>
                    {SYMBOLS.arch}{' '}
                </Text>
                <Text color={disabled ? 'gray' : INK_COLORS.primary}>
                    {SYMBOLS.prompt}{' '}
                </Text>
                {disabled ? (
                    <Text dimColor>Processing…</Text>
                ) : (
                    <>
                        {renderValueWithCursor()}
                        {!value && placeholder && (
                            <Text dimColor> {placeholder}</Text>
                        )}
                    </>
                )}
            </Box>

            {/* Multi-line hint — only when actually multi-line */}
            {!disabled && isMultiLine && (
                <Box marginLeft={4}>
                    <Text dimColor>
                        {SYMBOLS.chevronDown} multi-line · Enter to send · {'\\'} for newline
                    </Text>
                </Box>
            )}

            {/* Command suggestions — collapses gracefully when long */}
            {!disabled && totalSuggestions > 0 && (
                <Box marginLeft={4} marginTop={0} flexDirection="column">
                    {visibleSuggestions.map((suggestion, i) => (
                        <Box key={i}>
                            <Text dimColor>{SYMBOLS.chevronRight} </Text>
                            <Text color={INK_COLORS.success}>{suggestion}</Text>
                        </Box>
                    ))}
                    {hiddenCount > 0 && (
                        <Text dimColor>
                          + {hiddenCount} more · keep typing to filter
                        </Text>
                    )}
                    <Text dimColor>Tab to complete</Text>
                </Box>
            )}
        </Box>
    );
};
